import DateTime from 'luxon/src/datetime.js';

const isInRange = (from, to) => value => value >= from && value <= to;

const validateDateObjStructure = dateObj => {
  const { value } = dateObj;
  return Object.keys(value).every(key => !isNaN(+value[key]));
};

const dateObjToDateTime = dateObj => {
  return DateTime.fromObject({
    ...dateObj.getDateValue(),
    hour: 0,
    minute: 0,
    second: 0,
    zone: 'utc',
  });
};

const isValidDate = dateObj => {
  const isValidStructure = validateDateObjStructure(dateObj);
  if (!isValidStructure) return false;

  const dateTime = dateObjToDateTime(dateObj);
  const isValidDate = isInRange(1900, 2100)(dateTime.year) && dateTime.isValid;
  return isValidDate;
};

// rules
export const bool_question_required = {
  validate: value => {
    const valid = value === true || value === false;
    return {
      valid,
      data: {
        required: true,
      },
    };
  },
  computesRequired: true,
  immediate: false,
};

export const date_input_group = {
  validate: (dateObj, { isRequired, wasValidated }) => {
    const { isComplete, isEmpty } = dateObj.meta;
    const isValid = isValidDate(dateObj);

    if (!isRequired) {
      return (isComplete && isValid) || isEmpty;
    }
    return isValid && (isComplete || !wasValidated);
  },
  params: ['isRequired', 'wasValidated'],
};

export const min_date = {
  validate: (dateObj, { dateObjOther }) => {
    const { isComplete } = dateObj.meta;

    if (!isComplete || !dateObjOther || !dateObjOther.meta.isComplete) return true;

    const dateTime = dateObjToDateTime(dateObj).toISO();
    const dateTimeOther = dateObjToDateTime(dateObjOther).toISO();
    return dateTime >= dateTimeOther;
  },
  params: [{ name: 'dateObjOther', isTarget: true }],
};

export const max_date = {
  validate: (dateObj, { dateObjOther }) => {
    const { isComplete } = dateObj.meta;

    if (!isComplete || !dateObjOther || !dateObjOther.meta.isComplete) return true;

    const dateTime = dateObjToDateTime(dateObj).toISO();
    const dateTimeOther = dateObjToDateTime(dateObjOther).toISO();
    return dateTime <= dateTimeOther;
  },
  params: [{ name: 'dateObjOther', isTarget: true }],
};

export const min_date_iso = {
  validate: (value, { otherValue }) => {
    return !otherValue || value >= otherValue;
  },
  params: [{ name: 'otherValue', isTarget: true }],
};

export const max_date_iso = {
  validate: (value, { otherValue }) => {
    return !otherValue || value <= otherValue;
  },
  params: [{ name: 'otherValue', isTarget: true }],
};

export const birth_number = {
  validate: birthNumber => {
    if (!birthNumber) return false;

    birthNumber = birthNumber.trim();

    // Remove '/' character if present.
    if (birthNumber.length > 6 && birthNumber[6] === '/') {
      birthNumber = birthNumber.substring(0, 6) + birthNumber.substring(7);
    }

    // There is still invalid character present
    if (!/^[0-9]+$/.test(birthNumber)) return false;

    // Invalid number of digits.
    if (birthNumber.length !== 9 && birthNumber.length !== 10) return false;

    // INFO: No validation for year is needed

    // INFO: Validate month.
    //       Men have normal month number but when there is not enough control numbers then 20 can be added to month.
    //       Women have month + 50 but when there is not enough control numbers then 20 can be addes => +70 instead of +50.
    const month = +birthNumber.substr(2, 2);
    let valid =
      (month >= 1 && month <= 12) ||
      (month >= 21 && month <= 32) ||
      (month >= 51 && month <= 62) ||
      (month >= 71 && month <= 82);
    if (!valid) return false;

    const day = +birthNumber.substr(4, 2);
    valid = day >= 1 && day <= 31;
    if (!valid) return false;

    if (birthNumber.length === 10) {
      let rest = +birthNumber.substr(0, 9) % 11;
      if (rest === 10) rest = 0; // For 10 nothing is added
      return rest === +birthNumber.substr(-1); // Check calculated number is equal to number in birth number.
    } else if (birthNumber.length === 9) {
      const year = +birthNumber.substr(0, 2);
      return year <= 53;
    }

    return true;
  },
};

const bankNumberValidator = {
  weights: [1, 2, 4, 8, 5, 10, 9, 7, 3, 6],

  isValid(accountNumber) {
    if (!accountNumber || !accountNumber.trim()) {
      return false;
    }

    const parts = accountNumber.split('-');
    if (parts.length === 1) {
      return this.isSecondNumberValid(parts[0]);
    } else if (parts.length === 2) {
      return this.isFirstNumberValid(parts[0]) && this.isSecondNumberValid(parts[1]);
    }
    return false;
  },
  isSecondNumberValid(number) {
    return number.length >= 2 && number.length <= 10 && this.validateNumber(number);
  },
  isFirstNumberValid(number) {
    return number.length >= 1 && number.length <= 6 && this.validateNumber(number);
  },

  // Validates that it contains only digits
  // Validates that sum of digits multiplied by weights is divisible by 11.
  validateNumber(number) {
    if (!number.match(/^[0-9]*$/)) return false;

    const sum = number
      .split('')
      .reverse()
      .reduce((sum, ch, i) => sum + +ch * this.weights[i], 0);

    return sum % 11 === 0;
  },
};

export const bank_account = {
  validate: value => bankNumberValidator.isValid(value),
};

export const ispv_code = {
  validate: value => value.length === 30,
};
